import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Paper, Grid, TextField } from '@mui/material';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useNavigate } from 'react-router-dom';
import { DEAL_STAGES } from '../constants/dealStages';
import dashboardService from '../services/dashboardService';

ChartJS.register(ArcElement, Tooltip, Legend);

const WonDealsByLOBChart = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 6);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadChartData();
  }, [startDate, endDate]);

  const loadChartData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dashboardService.getWonDealsByLOB(startDate, endDate);
      setChartData(data);
    } catch (err) {
      console.error('Error loading won deals by LOB:', err);
      setError('Failed to load chart data');
    } finally {
      setLoading(false);
    }
  };

  // Process data for the chart
  const processedChartData = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return {
        labels: [],
        datasets: [{
          data: [],
          backgroundColor: [],
          borderColor: [],
          borderWidth: 2,
          hoverBackgroundColor: [],
          hoverBorderWidth: 3
        }],
        counts: [],
        lobData: {}
      };
    }

    // Prepare chart data
    const labels = chartData.map(item => item.LineOfBusiness);
    const values = chartData.map(item => parseFloat(item.TotalValue || 0));
    const counts = chartData.map(item => parseInt(item.DealCount || 0));

    // Generate colors for each segment
    const colors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0',
      '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF',
      '#4BC0C0', '#FF6384', '#36A2EB', '#FFCE56'
    ];

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors.slice(0, labels.length),
          borderColor: colors.slice(0, labels.length).map(color => color.replace('0.2', '1')),
          borderWidth: 2,
          hoverBackgroundColor: colors.slice(0, labels.length).map(color => color + 'CC'),
          hoverBorderWidth: 3
        }
      ],
      counts,
      lobData: chartData.reduce((acc, item, index) => {
        acc[item.LineOfBusiness] = {
          count: parseInt(item.DealCount || 0),
          totalValue: parseFloat(item.TotalValue || 0)
        };
        return acc;
      }, {})
    };
  }, [chartData]);

  // Chart options with custom tooltip and click handler
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed;
            const count = processedChartData.counts[context.dataIndex];
            const formattedValue = new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(value);
            
            return [
              `${label}`,
              `Deals: ${count}`,
              `Total Value: ${formattedValue}`,
              '',
              '💡 Click to view closed won opportunities'
            ];
          }
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.3)',
        borderWidth: 1,
        cornerRadius: 6,
        displayColors: false,
        padding: 12
      }
    },
    onHover: (event, activeElements) => {
      event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
    },
    onClick: (event, activeElements) => {
      if (activeElements.length > 0) {
        const clickedElementIndex = activeElements[0].index;
        const clickedLOB = processedChartData.labels[clickedElementIndex];
        
        // Navigate to opportunities with LOB, date range, and stage filters
        const searchParams = new URLSearchParams();
        searchParams.set('lob', clickedLOB);
        searchParams.set('startDate', startDate);
        searchParams.set('endDate', endDate);
        searchParams.set('timeRangeEnabled', 'true');
        searchParams.set('stage', 'Closed as Won');
        
        navigate(`/opportunities?${searchParams.toString()}`);
      }
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const totalValue = processedChartData.datasets[0]?.data.reduce((sum, value) => sum + value, 0) || 0;
  const totalDeals = processedChartData.counts?.reduce((sum, count) => sum + count, 0) || 0;

  if (loading) {
    return (
      <Paper sx={{ p: 3, height: 400 }}>
        <Typography variant="h6" gutterBottom>
          Won Deals by Line of Business
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <Typography>Loading...</Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, height: 400 }}>
        <Typography variant="h6" gutterBottom>
          Won Deals by Line of Business
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, height: 500 }}>
      <Typography variant="h6" gutterBottom>
        Won Deals by Line of Business
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
        Click on any slice to view closed won opportunities for that line of business
      </Typography>
      
      {/* Date Range Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
      </Grid>

      {/* Summary Stats */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Total Deals: <strong>{totalDeals}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Total Value: <strong>{formatCurrency(totalValue)}</strong>
        </Typography>
      </Box>

      {/* Chart */}
      <Box sx={{ height: 300, position: 'relative' }}>
        {processedChartData.labels.length > 0 ? (
          <Pie data={processedChartData} options={chartOptions} />
        ) : (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            color: 'text.secondary'
          }}>
            <Typography>No data available for the selected date range</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default WonDealsByLOBChart; 